import React, { useEffect, useMemo, useRef, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import Panel from "../../../components/ui/Panel/Panel.jsx";
import Button from "../../../components/ui/Button/Button.jsx";
import Tag from "../../../components/ui/Tag/Tag.jsx";
import Pagination from "../../../components/ui/Pagination/Pagination.jsx";
import Checkbox from "../../../components/forms/Checkbox/Checkbox.jsx";
import FormField from "../../../components/forms/FormField/FormField.jsx";
import Select from "../../../components/forms/Select/Select.jsx";
import TextInput from "../../../components/forms/TextInput/TextInput.jsx";
import BulkImportModal from "../../../components/bulk/BulkImportModal.jsx";
import ConfirmModal from "../../../components/ui/Modal/ConfirmModal.jsx";
import BulkDeleteBar from "../../../components/ui/BulkDeleteBar/BulkDeleteBar.jsx";
import TriStateCheckbox from "../../../components/ui/TriStateCheckbox/TriStateCheckbox.jsx";
import { RATES, WEIGHTS, visibleMonthOptions } from "../../../data/rates.js";
import { useApp } from "../../../hooks/useApp.js";
import { useRowSelection } from "../../../hooks/useRowSelection.js";
import { money } from "../../../utils/format.js";
import { isValidVin, validateExplanation, sanitizeVinInput, sanitizeExplanationInput } from "../../../utils/validators.js";
import { parseYesNo } from "../../../utils/bulkImport.js";
import { getWeightCategories, getFirstUsedMonths, getTaxYears } from "../../../services/lookupService.js";
import { getToken } from "../../../services/api.js";
import { saveFormType, updateReturnYearMonth } from "../../../services/returnService.js";
import {
  addVinCorrection,
  updateVinCorrection,
  deleteVinCorrection,
  deleteVinCorrectionsBulk,
  bulkAddVinCorrections,
} from "../../../services/trucksService.js";

const EXPLANATION_MAX = 9000; // efileTypes.xsd ExplanationType maxLength
const WEIGHT_CATEGORIES = Object.keys(RATES);
const PAGE_SIZE = 10;

// Matches TrucksTable's weightLabelFor - backend lookup rows ({ code,
// weightLabel }) take priority, falling back to the static WEIGHTS map.
const weightLabelFor = (cat, weightCategories) =>
  weightCategories.find((w) => w.code === cat)?.weightLabel ?? WEIGHTS[cat];

// Only TY2026 is offered for now - matches the current backend seed data
// (lookup_tax_years). Kept as the initial state so the field still renders
// immediately if the lookup fetch is slow or fails.
const DEFAULT_TAX_YEARS = [{ value: "2026" }];
const yearLabel = (year) => `${year}-${Number(year) + 1}`;

// VIN Correction (IRS2290.xsd VINCorrectionInd, referencing the required
// VINCorrectionExplanationStatement document / VINCorrectionExplanationStmt
// element - see VINCorrectionExplanationStatement.xsd). i2290: "Check the
// VIN Correction box if you are correcting a VIN listed on a previously
// filed Schedule 1... List the corrected VIN or VINs on Schedule 1... Attach
// a statement with an explanation for the VIN correction." A VIN correction
// reissues Schedule 1 with no tax due (F2290-066 requires the explanation).
// Trucks isn't a separate step for this filing type (see stepConfig.js), so
// this page collects both the wrong (original) VIN and the corrected one,
// plus the same category/logging/suspended detail Trucks collects - each
// Schedule 1 entry still needs a VIN and a category (IRS2290Schedule1.xsd
// VehicleReportTaxItem).
const VinCorrectionStep = ({ onNext, onBack }) => {
  const { state, dispatch, showToast } = useApp();
  const [showExplError, setShowExplError] = useState(false);
  const [oldVin, setOldVin] = useState("");
  const [vin, setVin] = useState("");
  const [cat, setCat] = useState("");
  const [logging, setLogging] = useState(false);
  const [suspended, setSuspended] = useState(false);
  const [oldVinError, setOldVinError] = useState(false);
  const [vinError, setVinError] = useState(false);
  const [catError, setCatError] = useState(false);
  const [rowsError, setRowsError] = useState(false);
  const [lookupsLoading, setLookupsLoading] = useState(true);
  const [continueSaving, setContinueSaving] = useState(false);
  const [rowSaving, setRowSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [pendingRemoveId, setPendingRemoveId] = useState(null);
  const [removeError, setRemoveError] = useState("");
  const [yearSaving, setYearSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [page, setPage] = useState(0);
  const [bulkOpen, setBulkOpen] = useState(false);
  // Client-side only, keyed by row id: vinCorrectionExpl is one shared
  // statement on the return (no per-row explanation column on the backend -
  // see the lastExplRef comment below), but the table/Edit both need to show
  // "what was typed for this vehicle" - captured at the moment each row is
  // added/updated. Doesn't survive a reload of an existing return (nothing
  // to fetch it back from); rows added before this feature, or via bulk
  // import (no explanation field in that flow), simply have no entry here.
  const [rowExplanations, setRowExplanations] = useState({});
  const isEditing = editingId != null;

  // Checkbox multi-select + bulk delete - separate from the single-row
  // pendingRemoveId/removeError state above, so the existing one-at-a-time
  // Delete flow is untouched. Rows here always carry a real id (server id
  // when usingBackend, else a client-generated Date.now()+random - see
  // addRow below), so selection is uniformly id-keyed regardless of mode.
  const selection = useRowSelection();
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkError, setBulkError] = useState("");

  const expl = state.vinCorrectionExpl;
  const rows = state.correctedVins;
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = rows.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
  const explError = validateExplanation(expl, EXPLANATION_MAX);
  const usingBackend = !!(getToken() && state.returnId);

  // Rejected-return-resubmission vehicle info (informational only, mirrors
  // TrucksStep's identical block) - adding more vehicles than originally filed
  // is allowed; an additional service fee applies before resubmitting,
  // enforced server-side at submit time.
  const hasRejectionInfo = state.rejectedResubmission && state.originalVehicleCount != null;
  const extraPendingFee = state.extraVehiclesPendingFee ?? 0;

  const formBusy = rowSaving || deletingId != null;

  // "Select all" covers every loaded row, not just the current page - the
  // whole list is already loaded client-side (Requirement 15).
  const allRowIds = rows.map((r) => r.id);
  const selectedOnScreen = allRowIds.reduce((n, id) => n + (selection.selectedIds.has(id) ? 1 : 0), 0);
  const headerChecked = allRowIds.length > 0 && selectedOnScreen === allRowIds.length;
  const headerIndeterminate = selectedOnScreen > 0 && selectedOnScreen < allRowIds.length;

  const monthOptions = state.monthsLookup.length
    ? state.monthsLookup.map((m) => [m.value, m.label])
    : visibleMonthOptions().map((m) => [m[0], m[1]]);

  const taxYearOptions = state.taxYearsLookup.length ? state.taxYearsLookup : DEFAULT_TAX_YEARS;

  // Rate shown switches between the standard and logging annual amount as
  // the Logging checkbox is toggled - same convention as TrucksStep.
  const catOptions = state.weightCategoriesLookup.length
    ? state.weightCategoriesLookup.map((w) => [
        w.code,
        `${w.code} · ${w.weightLabel} · ${money(logging ? w.annualLoggingAmt : w.annualNormalAmt)}/yr`,
      ])
    : WEIGHT_CATEGORIES.map((c) => [c, `${c} · ${WEIGHTS[c]} · ${money(RATES[c][logging ? 1 : 0])}/yr`]);

  const validCatCodes = useMemo(
    () => new Set(state.weightCategoriesLookup.length ? state.weightCategoriesLookup.map((w) => w.code) : WEIGHT_CATEGORIES),
    [state.weightCategoriesLookup]
  );

  // "CODE - weight range" strings for the bulk-upload template's Excel
  // dropdown - see TrucksStep's catDropdownOptions for why. parse (below)
  // strips the " - ..." part back off before saving.
  const catDropdownOptions = useMemo(
    () =>
      (state.weightCategoriesLookup.length
        ? state.weightCategoriesLookup.map((w) => [w.code, w.weightLabel])
        : WEIGHT_CATEGORIES.map((c) => [c, WEIGHTS[c]])
      )
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([code, label]) => `${code} - ${label}`),
    [state.weightCategoriesLookup]
  );

  // Bulk-upload columns for the corrected-vehicle table - keys match the
  // { oldVin, vin, cat, logging, suspended } shape addRow (manual) already
  // passes to addVinCorrection/dispatch, reused as-is by bulkAddRow below.
  const bulkColumns = useMemo(
    () => [
      {
        key: "oldVin",
        header: "Old VIN (as originally filed)",
        required: true,
        example: "1FUJGLDR9CLBP8834",
        description: "The VIN as it appears on the previously filed Schedule 1.",
        parse: (v) => sanitizeVinInput(String(v ?? "")),
        validate: (v) => (isValidVin(v) ? "" : "Not a valid VIN."),
      },
      {
        key: "vin",
        header: "Corrected VIN",
        required: true,
        example: "1FUJGLDR9CLBP9999",
        description: "The corrected VIN. Must be different from the old VIN.",
        parse: (v) => sanitizeVinInput(String(v ?? "")),
        validate: (v, row) => {
          if (!isValidVin(v)) return "Not a valid VIN.";
          if (v === row.oldVin) return "Must be different from the old VIN.";
          return "";
        },
      },
      {
        key: "cat",
        header: "Weight category (A-W)",
        required: true,
        example: "D",
        description: "Single letter A-W weight category. Leave blank if Suspended = Yes (it's set to W automatically). Pick from the dropdown - it shows each code's weight range.",
        dropdown: catDropdownOptions,
        parse: (v) => String(v ?? "").trim().split(" - ")[0].trim().toUpperCase(),
        validate: (v, row) => {
          if (row.suspended) return "";
          if (!v) return "Weight category is required unless Suspended = Yes.";
          if (!validCatCodes.has(v)) return "Not a recognized weight category.";
          return "";
        },
      },
      {
        key: "logging",
        header: "Logging (Yes/No)",
        required: false,
        example: "No",
        description: "Yes if this truck is used exclusively for hauling harvested forest products (lower tax rate).",
        parse: parseYesNo,
      },
      {
        key: "suspended",
        header: "Suspended (Yes/No)",
        required: false,
        example: "No",
        description: "Yes if the truck is expected to stay under the mileage limit - listed but not taxed (category W).",
        parse: parseYesNo,
      },
    ],
    [validCatCodes, catDropdownOptions]
  );

  // Same usingBackend/local-fallback branch addRow uses, minus the per-field
  // form state - called once per parsed row by BulkImportModal.
  const bulkAddRow = async (payload) => {
    if (usingBackend) {
      const r = await addVinCorrection(state.returnId, payload);
      // vinCorrectionExpl comes back from a ROW-level endpoint, so it's
      // whatever was last SAVED (via Continue/saveFormType below), not
      // necessarily what's currently typed in the textarea - passing the
      // live local value here keeps SET_RETURN's `action.vinCorrectionExpl
      // || state.vinCorrectionExpl` fallback from replacing an unsaved edit
      // with a stale server value the moment a row is added.
      // returnType is the same story: formTypeCd on the return itself isn't
      // written to "vincorrection" until this step's Continue (saveFormType)
      // - a row-add before that still echoes back whatever formTypeCd the
      // return had before (typically "original"), which would otherwise
      // overwrite state.returnType and reroute the wizard mid-step (stepConfig
      // recomputes the step sequence off state.returnType). This step only
      // renders when returnType is already "vincorrection", so pin it.
      dispatch({ type: "SET_RETURN", ...r, vinCorrectionExpl: expl, returnType: "vincorrection" });
      syncVinCorrectionFormType(r.correctedVins);
      return r;
    }
    dispatch({ type: "ADD_CORRECTED_VIN", vehicle: { id: Date.now() + Math.random(), ...payload } });
    return payload;
  };

  // Real bulk endpoint (one request for every row) - used instead of
  // bulkAddRow's per-row loop whenever a backend return is loaded.
  const bulkAdd = async (rows) => {
    const r = await bulkAddVinCorrections(state.returnId, rows);
    dispatch({ type: "SET_RETURN", ...r, vinCorrectionExpl: expl, returnType: "vincorrection" });
    syncVinCorrectionFormType(r.correctedVins);
    return r.results;
  };

  useEffect(() => {
    let cancelled = false;
    Promise.all([getFirstUsedMonths(), getWeightCategories(), getTaxYears()])
      .then(([months, weightCategories, taxYears]) => {
        if (cancelled) return;
        if (Array.isArray(months) && months.length) dispatch({ type: "SET_LOOKUPS", months });
        if (Array.isArray(weightCategories) && weightCategories.length) dispatch({ type: "SET_LOOKUPS", weightCategories });
        if (Array.isArray(taxYears) && taxYears.length) {
          dispatch({ type: "SET_LOOKUPS", taxYears: taxYears.map((y) => ({ value: y.code })) });
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLookupsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleYearChange = async (e) => {
    const year = e.target.value;
    if (usingBackend) {
      setYearSaving(true);
      try {
        const r = await updateReturnYearMonth(state.returnId, { taxYear: year, firstUsedMonth: state.month });
        dispatch({ type: "SET_RETURN", ...r });
      } catch (err) {
        showToast(err.message || "Couldn't save the tax year. Please try again.");
      } finally {
        setYearSaving(false);
      }
      return;
    }
    dispatch({ type: "SET_TAX_YEAR", year });
  };

  // Remembers the last non-blank explanation the filer typed, so it can be
  // restored by startEdit below after clearExplAfterAdd has blanked the
  // field - vinCorrectionExpl is one shared statement for the whole return
  // (TaxReturn.vin_correction_expl, only persisted on Continue), not stored
  // per row, so there's no per-vehicle value to fetch back; this is the
  // closest a filer gets to "the value I typed" reappearing on Edit.
  const lastExplRef = useRef("");

  const setExpl = (value) => {
    const clean = sanitizeExplanationInput(value);
    dispatch({ type: "SET_VIN_CORRECTION_EXPL", text: clean });
    if (clean.trim()) lastExplRef.current = clean;
    if (showExplError) setShowExplError(false);
  };

  // Best-effort: commits formTypeCd="vincorrection" + the explanation to the
  // backend as soon as both saveFormType requirements exist (an explanation
  // and at least one corrected vehicle) - rather than waiting for this
  // step's own Continue (see handleContinue below). Without this, a filer
  // who adds a row and leaves via My Filings before Continue resumes into a
  // return the backend still thinks is "original": My Filings shows "Not
  // started" (TaxReturnServiceImpl's hasVehicleContent only checks the
  // vin_corrections table once formTypeCd already says "vincorrection") and
  // the picked filing type doesn't show as selected. Silently gives up on
  // failure - the next add, or Continue, retries with a complete payload.
  const syncVinCorrectionFormType = async (correctedVins) => {
    if (!usingBackend) return;
    const currentExpl = (expl.trim() ? expl : lastExplRef.current).trim();
    if (!currentExpl || !correctedVins?.length) return;
    try {
      const full = await saveFormType(state.returnId, {
        returnType: "vincorrection",
        addressChange: state.addressChange,
        vinCorrectionExpl: currentExpl,
        correctedVins,
        taxPreparerId: state.returnTaxPreparerId,
      });
      dispatch({ type: "SET_RETURN", ...full, vinCorrectionExpl: expl, returnType: "vincorrection" });
    } catch {
      // best-effort - see comment above
    }
  };

  const resetForm = () => {
    setOldVin("");
    setVin("");
    setCat("");
    setLogging(false);
    setSuspended(false);
    setOldVinError(false);
    setVinError(false);
    setCatError(false);
    setEditingId(null);
  };

  // Explicit choice: the explanation is otherwise one statement shared
  // across every corrected vehicle on this VIN correction, but per-add
  // clearing was requested anyway - it must be re-entered before the next
  // truck is added, and before Continue if this was the last one.
  const clearExplAfterAdd = () => dispatch({ type: "SET_VIN_CORRECTION_EXPL", text: "" });

  // Loads an existing corrected-VIN row back into the draft form for
  // editing - same pattern as TrucksStep's startEdit.
  const startEdit = (row) => {
    setOldVin(row.oldVin || "");
    setVin(row.vin || "");
    setCat(row.suspended ? "" : row.cat || "");
    setLogging(!!row.logging);
    setSuspended(!!row.suspended);
    setOldVinError(false);
    setVinError(false);
    setCatError(false);
    setEditingId(row.id);
    // Prefer the explanation actually typed for THIS row (rowExplanations);
    // fall back to the last-typed value generally (rows added before this
    // feature, or via bulk import, have no per-row entry) rather than
    // leaving Edit open on a blank box.
    const savedExpl = rowExplanations[row.id];
    if (savedExpl != null) {
      dispatch({ type: "SET_VIN_CORRECTION_EXPL", text: savedExpl });
    } else if (!expl.trim() && lastExplRef.current) {
      dispatch({ type: "SET_VIN_CORRECTION_EXPL", text: lastExplRef.current });
    }
  };

  // Backend field errors from the per-row add/update endpoints come back as
  // flat field names (oldVin/vin/categoryCd - no "vinCorrections[i]." prefix,
  // since each call is for a single row), unlike applyFieldErrors below which
  // handles the batch saveFormType shape.
  const applyRowFieldErrors = (err) => {
    const fieldErrors = err?.body?.error?.errors;
    if (!Array.isArray(fieldErrors)) return;
    fieldErrors.forEach((fe) => {
      if (fe.field === "oldVin") setOldVinError(true);
      else if (fe.field === "vin") setVinError(true);
      else if (fe.field === "categoryCd") setCatError(true);
    });
  };

  const addRow = async () => {
    const oldV = oldVin.trim().toUpperCase();
    const newV = vin.trim().toUpperCase();
    const oldOk = isValidVin(oldV);
    const newOk = isValidVin(newV) && newV !== oldV;
    setOldVinError(!oldOk);
    setVinError(!newOk);
    if (!oldOk || !newOk) return;

    // Mirrors the backend's DUPLICATE_VIN check (VinCorrectionServiceImpl) so
    // a repeat shows up immediately instead of only after a round trip -
    // same pattern as TrucksStep/AmendedReturnStep's local duplicate check.
    const duplicateVin = rows.some((r) => r.id !== editingId && r.vin === newV);
    const duplicateOldVin = rows.some((r) => r.id !== editingId && r.oldVin === oldV);
    if (duplicateVin) setVinError(true);
    if (duplicateOldVin) setOldVinError(true);
    if (duplicateVin || duplicateOldVin) {
      showToast(
        duplicateVin
          ? "That corrected VIN is already used on this return."
          : "That VIN is already being corrected on this return."
      );
      return;
    }

    if (!suspended && !cat) {
      setCatError(true);
      return;
    }
    const patch = { oldVin: oldV, vin: newV, cat: suspended ? "W" : cat, logging, suspended };
    const explAtSave = expl.trim();

    if (usingBackend) {
      setRowSaving(true);
      try {
        const r = isEditing
          ? await updateVinCorrection(editingId, patch)
          : await addVinCorrection(state.returnId, patch);
        // See bulkAddRow above: preserve the currently-typed explanation and
        // pin returnType - don't let this row-level response's (possibly
        // stale, pre-Continue) saved values clobber an unsaved edit or
        // reroute the wizard out of this step.
        dispatch({ type: "SET_RETURN", ...r, vinCorrectionExpl: expl, returnType: "vincorrection" });
        syncVinCorrectionFormType(r.correctedVins);
        if (r.row?.id != null) {
          setRowExplanations((prev) => ({ ...prev, [r.row.id]: explAtSave }));
        }
        resetForm();
        if (!isEditing) clearExplAfterAdd();
        setRowsError(false);
      } catch (err) {
        applyRowFieldErrors(err);
        showToast(err.message || "Couldn't save the corrected vehicle. Please try again.");
      } finally {
        setRowSaving(false);
      }
      return;
    }

    if (isEditing) {
      dispatch({ type: "UPDATE_CORRECTED_VIN", id: editingId, patch });
      setRowExplanations((prev) => ({ ...prev, [editingId]: explAtSave }));
    } else {
      const localId = Date.now();
      dispatch({ type: "ADD_CORRECTED_VIN", vehicle: { id: localId, ...patch } });
      setRowExplanations((prev) => ({ ...prev, [localId]: explAtSave }));
    }
    resetForm();
    if (!isEditing) clearExplAfterAdd();
    setRowsError(false);
  };

  const removeRow = (id) => {
    if (usingBackend) {
      setRemoveError("");
      setPendingRemoveId(id);
      return;
    }
    dispatch({ type: "REMOVE_CORRECTED_VIN", id });
    setRowExplanations((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    if (editingId === id) resetForm();
  };

  const cancelRemoveRow = () => {
    setPendingRemoveId(null);
    setRemoveError("");
  };

  const confirmRemoveRow = async () => {
    const id = pendingRemoveId;
    if (id == null) return;
    setDeletingId(id);
    try {
      const r = await deleteVinCorrection(id);
      // See bulkAddRow above: preserve the currently-typed explanation and
      // pin returnType.
      dispatch({ type: "SET_RETURN", ...r, vinCorrectionExpl: expl, returnType: "vincorrection" });
      setRowExplanations((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      if (editingId === id) resetForm();
      setPendingRemoveId(null);
      setRemoveError("");
    } catch (err) {
      setRemoveError(err.message || "Couldn't delete the corrected vehicle. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  // Bulk delete - selection.selectedIds holds real row ids either way (see
  // the selection hook's setup comment above), so unlike Trucks/Amended-
  // return there's no index-vs-id branch here - only whether the delete
  // itself goes through the backend or stays local. Always confirms first
  // regardless of mode, unlike single-row delete.
  const openBulkDelete = () => {
    setBulkError("");
    setBulkConfirmOpen(true);
  };

  const cancelBulkDelete = () => {
    setBulkConfirmOpen(false);
    setBulkError("");
  };

  const confirmBulkDelete = async () => {
    const count = selection.count;
    const ids = selection.selectedIds;
    setBulkDeleting(true);
    try {
      if (usingBackend) {
        const r = await deleteVinCorrectionsBulk(state.returnId, Array.from(ids));
        dispatch({ type: "SET_RETURN", ...r, vinCorrectionExpl: expl, returnType: "vincorrection" });
      } else {
        dispatch({ type: "BULK_REMOVE_CORRECTED_VINS", ids });
      }
      setRowExplanations((prev) => {
        const next = { ...prev };
        ids.forEach((id) => delete next[id]);
        return next;
      });
      if (editingId != null && ids.has(editingId)) resetForm();
      selection.clear();
      setBulkConfirmOpen(false);
      setBulkError("");
      showToast(`${count} vehicle${count === 1 ? "" : "s"} deleted successfully.`);
    } catch (err) {
      setBulkError(err.message || "Unable to delete the selected vehicles. Please try again.");
    } finally {
      setBulkDeleting(false);
    }
  };

  // Backend field errors here (e.g. "vinCorrections[1].vin") don't have a
  // per-row slot in this step's read-only table the way WeightIncreaseStep's
  // editable rows do, so they're surfaced via the rowsError/showExplError
  // banners plus the toast message rather than inline per-row - the
  // client-side checks in addRow already prevent this in normal use.
  const applyFieldErrors = (err) => {
    const fieldErrors = err?.body?.error?.errors;
    if (!Array.isArray(fieldErrors)) return;
    fieldErrors.forEach((fe) => {
      if (fe.field === "vinCorrectionExpl") setShowExplError(true);
      else if (fe.field === "vinCorrections" || /^vinCorrections\[/.test(fe.field || "")) setRowsError(true);
    });
  };

  const handleContinue = async () => {
    // clearExplAfterAdd blanks the shared box after every "+ Add truck" - if
    // nothing's been retyped since, the filer's last entry is still sitting
    // in lastExplRef (see setExpl above); use it to validate/save so Continue
    // doesn't demand a retype of text already entered once. Deliberately NOT
    // written back into state.vinCorrectionExpl here - the box must stay
    // visibly blank (matches the clear-after-Add behavior); only startEdit
    // repopulates the visible field, Continue does not.
    const currentExpl = (expl.trim() ? expl : lastExplRef.current).trim();

    let ok = true;
    if (validateExplanation(currentExpl, EXPLANATION_MAX)) {
      setShowExplError(true);
      ok = false;
    }
    if (!rows.length) {
      setRowsError(true);
      ok = false;
    }
    if (!ok) return;

    if (usingBackend) {
      setContinueSaving(true);
      try {
        const r = await saveFormType(state.returnId, {
          returnType: "vincorrection",
          addressChange: state.addressChange,
          vinCorrectionExpl: currentExpl,
          correctedVins: rows,
          taxPreparerId: state.returnTaxPreparerId,
        });
        dispatch({ type: "SET_RETURN", ...r });
      } catch (err) {
        setContinueSaving(false);
        applyFieldErrors(err);
        showToast(err.message || "Couldn't save. Please try again.");
        return;
      }
      setContinueSaving(false);
    }

    onNext();
  };

  return (
    <Panel padded>
      <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
        VIN correction
      </div>
      <h2 className="text-2xl m-[4px_0]">Why are you correcting this VIN?</h2>
      <p className="text-[var(--muted)] max-w-[62ch] mb-4">
        A VIN correction reissues your Schedule 1 — <b>no tax is due</b>. Use the form for the tax period you're
        correcting.
      </p>

      <div className="grid gap-3.5 sm:grid-cols-2" style={{ maxWidth: 560 }}>
        <FormField label="Tax year" hint={yearSaving ? "Saving…" : undefined}>
          <Select value={state.year} onChange={handleYearChange} disabled={lookupsLoading || yearSaving}>
            {taxYearOptions.map((y) => (
              <option key={y.value} value={y.value}>
                {yearLabel(y.value)}
              </option>
            ))}
          </Select>
        </FormField>

        <FormField label="Month first used (this return)" hint="Sets the tax period being corrected.">
          <Select
            value={state.month}
            onChange={(e) => dispatch({ type: "SET_MONTH", month: e.target.value })}
            disabled={lookupsLoading}
          >
            {monthOptions.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </FormField>
      </div>

      {hasRejectionInfo && (
        <div
          className="rounded-[var(--r-sm)] border p-3 mb-4 mt-4 text-[13px]"
          style={
            extraPendingFee > 0
              ? { borderColor: "var(--amber)", background: "var(--amber-soft)", color: "var(--amber)" }
              : { borderColor: "var(--line-2)", background: "var(--panel-2)", color: "var(--muted)" }
          }
        >
          <b className="font-semibold" style={{ color: extraPendingFee > 0 ? "var(--amber)" : "var(--ink)" }}>
            Vehicles: {rows.length} (originally filed: {state.originalVehicleCount})
          </b>
          <div className="mt-0.5">
            {extraPendingFee > 0
              ? `You've added ${extraPendingFee} vehicle${extraPendingFee === 1 ? "" : "s"} beyond what was originally filed — an additional service fee applies before this return can be resubmitted (you'll pay it on the Payment Details step).`
              : `This return was rejected by the IRS. You can add, remove, or correct vehicles freely — adding more than the ${state.originalVehicleCount} originally filed will require an additional service fee before resubmitting.`}
          </div>
        </div>
      )}

      <h3 className="text-[15px] font-semibold mt-2 mb-1">
        Corrected vehicle(s) <span className="text-[var(--danger)]">*</span>
      </h3>
      <p className="text-[var(--muted)] text-sm mb-2.5">Every corrected VIN goes on the reissued Schedule 1.</p>

      <Panel tone="soft" className="!shadow-none p-[18px] mb-3">
        <div className="flex items-center justify-between mb-3.5">
          <h3 className="text-[19px]">
            {isEditing ? "Update this vehicle" : "Add a vehicle"}
          </h3>
          {!isEditing && (
            <Button variant="ghost" onClick={() => setBulkOpen(true)} disabled={formBusy}>
              ⇪ Bulk upload
            </Button>
          )}
        </div>
        <div className="grid gap-3.5 sm:grid-cols-2">
          <FormField
            label="Old VIN (as originally filed)"
            required
            error="Enter a valid VIN."
            showError={oldVinError}
          >
            <TextInput
              mono
              maxLength={19}
              placeholder="VIN on the previously filed Schedule 1"
              value={oldVin}
              onChange={(e) => setOldVin(sanitizeVinInput(e.target.value))}
              invalid={oldVinError}
              disabled={lookupsLoading || formBusy}
            />
          </FormField>
          <FormField
            label="Corrected VIN"
            required
            error="Enter a valid VIN different from the old one."
            showError={vinError}
          >
            <TextInput
              mono
              maxLength={19}
              placeholder="Corrected VIN"
              value={vin}
              onChange={(e) => setVin(sanitizeVinInput(e.target.value))}
              invalid={vinError}
              disabled={lookupsLoading || formBusy}
            />
          </FormField>
        </div>

        <FormField label="Weight category" required={!suspended} error="Pick a category." showError={catError}>
          <Select
            value={cat}
            disabled={suspended || lookupsLoading || formBusy}
            onChange={(e) => {
              setCat(e.target.value);
              setCatError(false);
            }}
            invalid={catError}
          >
            <option value="">Select weight category…</option>
            {catOptions.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </FormField>

        <Checkbox
          id="vinCorrLog"
          title="Logging truck"
          subtitle="Hauls harvested forest products — pays 25% less."
          checked={logging}
          onChange={(e) => setLogging(e.target.checked)}
          disabled={formBusy}
        />
        <Checkbox
          id="vinCorrSusp"
          title="Suspended (low mileage)"
          subtitle="Expected 5,000 miles or less (7,500 for farm trucks). Listed but not taxed — category W."
          checked={suspended}
          onChange={(e) => {
            setSuspended(e.target.checked);
            if (e.target.checked) setCat("");
          }}
          disabled={formBusy}
        />

        <div className="flex gap-2">
          <Button variant="ghost" onClick={addRow} disabled={formBusy}>
            {rowSaving ? "Saving…" : isEditing ? "Update vehicle" : "+ Add truck"}
          </Button>
          {isEditing && (
            <Button variant="ghost" onClick={resetForm} disabled={formBusy}>
              Cancel
            </Button>
          )}
        </div>
      </Panel>

      <FormField
        label="Explanation for VIN correction"
        required
        hint={`e.g. A digit was transposed on the originally filed Schedule 1. ${expl.length}/${EXPLANATION_MAX} characters.`}
        error={explError}
        showError={showExplError}
      >
        <textarea
          className={[
            "w-full border rounded-[var(--r-sm)] px-3 py-[11px] bg-[var(--panel)] text-[var(--ink)] text-[15px] font-body focus:outline-2 focus:outline-[var(--accent)] focus:outline-offset-1",
            showExplError && explError ? "border-[var(--danger)]" : "border-[var(--line-2)]",
          ].join(" ")}
          rows={4}
          maxLength={EXPLANATION_MAX}
          placeholder="Explain why the VIN needs to be corrected…"
          value={expl}
          onChange={(e) => setExpl(e.target.value)}
        />
      </FormField>

      <BulkDeleteBar count={selection.count} onDelete={openBulkDelete} loading={bulkDeleting} className="mt-3" />
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="text-left p-3 border-b border-[var(--line)] w-[36px]">
              <TriStateCheckbox
                checked={headerChecked}
                indeterminate={headerIndeterminate}
                onChange={() => selection.selectAll(headerChecked ? [] : allRowIds)}
                label="Select all corrected vehicles"
              />
            </th>
            {["#", "Old VIN", "Corrected VIN", "Category", "Type", "Actions"].map((h) => (
              <th
                key={h}
                className="text-left p-3 border-b border-[var(--line)] font-disp uppercase tracking-[.07em] text-[11px] text-[var(--muted)] font-semibold"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={7} className="text-[var(--muted)] p-[22px_12px]">
                No corrected vehicles yet. Add your first one above.
              </td>
            </tr>
          ) : (
            pageRows.map((r, pageIndex) => (
              <tr key={r.id} className="hover:bg-[var(--accent-soft)] transition-colors">
                <td className="text-left p-3 border-b border-[var(--line)]">
                  <TriStateCheckbox
                    checked={selection.selectedIds.has(r.id)}
                    onChange={() => selection.toggle(r.id)}
                    label={`Select row ${safePage * PAGE_SIZE + pageIndex + 1}`}
                  />
                </td>
                <td className="text-left p-3 border-b border-[var(--line)]">{safePage * PAGE_SIZE + pageIndex + 1}</td>
                <td className="text-left p-3 border-b border-[var(--line)] font-mono text-[13px]">{r.oldVin}</td>
                <td className="text-left p-3 border-b border-[var(--line)] font-mono text-[13px]">{r.vin}</td>
                <td className="text-left p-3 border-b border-[var(--line)]">
                  {r.suspended ? "W" : r.cat}
                  {" · "}
                  {r.suspended ? "suspended" : weightLabelFor(r.cat, state.weightCategoriesLookup)}
                </td>
                <td className="text-left p-3 border-b border-[var(--line)]">
                  <div className="flex gap-1 flex-wrap">
                    {r.suspended && <Tag tone="susp">Suspended</Tag>}
                    {r.logging && <Tag tone="log">Logging</Tag>}
                    {!r.suspended && !r.logging && <Tag>Standard</Tag>}
                  </div>
                </td>
                <td className=" p-3 border-b border-[var(--line)] whitespace-nowrap">
                  <button
                    type="button"
                    title="Edit"
                    onClick={() => startEdit(r)}
                    disabled={formBusy}
                    className="inline-flex items-center justify-center border-0 bg-transparent text-[var(--dim)] cursor-pointer p-1 hover:text-[var(--accent)] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    title="Delete"
                    onClick={() => removeRow(r.id)}
                    disabled={formBusy}
                    className="inline-flex items-center justify-center border-0 bg-transparent text-[var(--dim)] cursor-pointer p-1 ml-1 hover:text-[var(--danger)] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <Pagination page={safePage} pageCount={pageCount} onChange={setPage} />
      {rowsError && <div className="text-xs text-[var(--danger)] mb-3 mt-3">Add at least one corrected vehicle.</div>}

      <div className="flex justify-between gap-3 mt-6">
        <Button variant="ghost" onClick={onBack}>
          ← Back
        </Button>
        <Button onClick={handleContinue} disabled={continueSaving || formBusy}>
          {continueSaving ? "Saving…" : "Continue →"}
        </Button>
      </div>

      <BulkImportModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        title="Bulk upload corrected vehicles"
        columns={bulkColumns}
        filename="form2290-vin-corrections-template.xlsx"
        sheetName="VIN Corrections"
        addRow={bulkAddRow}
        bulkAdd={usingBackend ? bulkAdd : undefined}
        templateId="form2290-vin-correction-v1"
      />

      <ConfirmModal
        open={pendingRemoveId != null}
        title="Delete Confirmation"
        confirmLabel="Delete"
        loading={deletingId === pendingRemoveId}
        error={removeError}
        onConfirm={confirmRemoveRow}
        onCancel={cancelRemoveRow}
      >
        Are you sure you want to delete this record? This action cannot be undone.
      </ConfirmModal>

      <ConfirmModal
        open={bulkConfirmOpen}
        title="Delete Selected Records?"
        confirmLabel="Delete"
        loading={bulkDeleting}
        error={bulkError}
        onConfirm={confirmBulkDelete}
        onCancel={cancelBulkDelete}
      >
        Are you sure you want to delete these {selection.count} record{selection.count === 1 ? "" : "s"}? This action
        cannot be undone.
      </ConfirmModal>
    </Panel>
  );
};

export default VinCorrectionStep;
